import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AuthLayout } from '@cold-ambulance/layout';
import { Button, CardContent } from '@cold-ambulance/shadcn';
import {
  FieldMessage,
  FormMessage,
  UploadField,
  errorToFormIssue,
  fileField,
  uploadFile,
  useAction,
} from '@cold-ambulance/ui';

import { type Step, StepCircles } from './stepper.tsx';

const documentsSchema = z.object({
  commercialRegistrationFile: fileField,
  taxCertificate: fileField,
  nationalAddressCertificate: fileField,
});

export default function SignupDocuments({
  step,
  index,
  onBack,
  onSubmitSuccess,
  newUser,
}: {
  step: Step;
  index: number;
  onBack?: () => void;
  onSubmitSuccess?: () => void;
  newUser?: boolean;
}) {
  const { t } = useTranslation();
  const updateVendorMutation = useAction('PATCH /vendor/me', {
    onSuccess: onSubmitSuccess,
  });

  const form = useForm({
    defaultValues: {
      commercialRegistrationFile: null as File | null,
      taxCertificate: null as File | null,
      nationalAddressCertificate: null as File | null,
    },
    validators: {
      onSubmit: documentsSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          const [
            commercialRegistrationId,
            taxCertificateId,
            nationalAddressCertificateId,
          ] = await Promise.all([
            uploadFile(value.commercialRegistrationFile!),
            uploadFile(value.taxCertificate!),
            uploadFile(value.nationalAddressCertificate!),
          ]);

          await updateVendorMutation.mutateAsync({
            commercialRegistrationFile: commercialRegistrationId,
            taxCertificate: taxCertificateId,
            nationalAddressCertificate: nationalAddressCertificateId,
          });

          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <AuthLayout
      title={t('signup.registerWithUs')}
      className="border"
      onBack={onBack}
    >
      <StepCircles step={step} index={index} />
      <p className="mt-6 font-bold">{t('signup.requiredDocuments')}</p>

      <CardContent className="px-0">
        <form
          className="mt-4 mb-4 space-y-6"
          onSubmit={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="commercialRegistrationFile"
            children={(field) => (
              <div className="space-y-2">
                <UploadField
                  label={t('profile.commercialRegistration')}
                  name={field.name}
                  value={field.state.value}
                  onChange={(file) => {
                    field.handleChange(file);

                    field.handleBlur();
                  }}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="taxCertificate"
            children={(field) => (
              <div className="space-y-2">
                <UploadField
                  label={t('profile.taxCertificate')}
                  name={field.name}
                  value={field.state.value}
                  onChange={(file) => {
                    field.handleChange(file);

                    field.handleBlur();
                  }}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="nationalAddressCertificate"
            children={(field) => (
              <div className="space-y-2">
                <UploadField
                  label={t('profile.nationalAddressCertificate')}
                  name={field.name}
                  value={field.state.value}
                  onChange={(file) => {
                    field.handleChange(file);
                    field.handleBlur();
                  }}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="mt-8 w-full cursor-pointer"
              >
                {isSubmitting ? t('signup.continuing') : t('common.next')}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
